const ENHANCED_ATTR = 'data-ap-gesture-sheet';

function getBackdrop() {
  return document.querySelector('.modal-overlay.is-visible, .ap-overlay.is-visible');
}

function setSheetY(sheet, y) {
  sheet.style.setProperty('--ap-modal-y', `${Math.max(0, y)}px`);
}

function setBackdropProgress(y) {
  const backdrop = getBackdrop();
  if (!backdrop) return;

  const alpha = Math.max(0.08, 0.34 - y / 760);
  backdrop.style.background = `rgba(0,0,0,${alpha.toFixed(3)})`;
}

function resetBackdrop() {
  const backdrop = getBackdrop();
  if (backdrop) backdrop.style.background = '';
}

function getSheetY(sheet) {
  return Number.parseFloat(sheet.style.getPropertyValue('--ap-modal-y')) || 0;
}

function getCompactVisibleHeight(sheet) {
  const raw = Number.parseFloat(sheet.getAttribute('data-ap-compact-height') || '');
  return Number.isFinite(raw) && raw > 0 ? raw : 195;
}

function supportsCompactSnap(sheet) {
  return sheet?.id === 'modalMtaLines' || sheet?.getAttribute?.('data-ap-compact-snap') === '1';
}

function getCompactY(sheet) {
  const sheetHeight = Math.max(1, sheet.getBoundingClientRect().height);
  return Math.max(0, sheetHeight - getCompactVisibleHeight(sheet));
}

function setUiBusy(value) {
  if (window.APUI?.setInteracting) {
    window.APUI.setInteracting(value);
  } else {
    window.__AP_UI_INTERACTING__ = Boolean(value);
  }
}

function animateSheet(sheet, toY, options = {}) {
  const fromY = Number.parseFloat(sheet.style.getPropertyValue('--ap-modal-y')) || 0;
  const duration = options.duration ?? 280;
  const easing = options.easing || ((t) => 1 - ((1 - t) ** 3));
  const startedAt = performance.now();

  if (sheet._apSheetAnimation) {
    cancelAnimationFrame(sheet._apSheetAnimation);
    sheet._apSheetAnimation = 0;
  }

  return new Promise((resolve) => {
    const tick = (now) => {
      const progress = Math.min(1, (now - startedAt) / duration);
      const eased = typeof easing === 'function' ? easing(progress) : progress;
      const y = fromY + (toY - fromY) * eased;

      setSheetY(sheet, y);
      setBackdropProgress(y);

      if (progress < 1) {
        sheet._apSheetAnimation = requestAnimationFrame(tick);
        return;
      }

      sheet._apSheetAnimation = 0;
      sheet.style.setProperty('--ap-modal-y', `${toY}px`);
      resolve();
    };

    sheet._apSheetAnimation = requestAnimationFrame(tick);
  });
}

function isInteractiveTarget(target) {
  return Boolean(target?.closest?.('a,button,input,textarea,select,[contenteditable="true"]'));
}

function isDraggableInteractiveTarget(sheet, target) {
  if (sheet.id !== 'modalFavorites') return false;
  return Boolean(target?.closest?.('.ap-favorites-route-card, .ap-favorites-stop-card'));
}

/** Only the message list scrolls; sheet drag-to-close must not steal vertical swipes there. */
function isMtaChatScrollZone(target) {
  if (!target?.closest) return false;
  if (target.closest('#mta-chat-list')) return true;
  const sticker = target.closest('#ap-mta-chat-sticker-overlay');
  if (sticker?.classList?.contains('ap-mta-chat-sticker-overlay--open')) return true;
  return false;
}

/** Scroll offset of the sheet body; drag-to-dismiss only when this is ~0 (see move() content branch). */
function getModalBodyScrollTop(sheet) {
  if (!sheet?.querySelector) return 0;
  const body = sheet.querySelector('.modal-content, .ap-modal-content');
  const inner = body ? body.scrollTop : 0;
  const outer = sheet.scrollTop || 0;
  return Math.max(inner, outer);
}

function closeSheet(sheet) {
  const targetY = Math.max(window.innerHeight, sheet.getBoundingClientRect().height + 96);

  setUiBusy(true);
  sheet.classList.add('ap-modal-closing');
  return animateSheet(sheet, targetY, {
    duration: 230,
    easing: (t) => t * t,
  }).then(() => {
    sheet.classList.remove('ap-modal-closing');
    sheet.classList.remove('ap-modal-compact');
    if (window.APUI?.modal?.close) {
      window.APUI.modal.close(sheet);
    } else {
      sheet.classList.remove('open', 'is-open');
      sheet.style.display = '';
    }
    resetBackdrop();
    setUiBusy(false);
  });
}

function settleSheet(sheet, toY = 0) {
  setUiBusy(true);
  sheet.classList.add('ap-modal-settling');
  sheet.classList.toggle('ap-modal-compact', toY > 0);
  return animateSheet(sheet, toY, {
    duration: 360,
    easing: (t) => {
      const c1 = 1.70158;
      const c3 = c1 + 1;
      return 1 + c3 * ((t - 1) ** 3) + c1 * ((t - 1) ** 2);
    },
  }).then(() => {
    sheet.classList.remove('ap-modal-settling');
    resetBackdrop();
    setUiBusy(false);
  });
}

export function enhanceBottomSheet(sheet) {
  if (!sheet) return null;
  if (sheet._apGestureSheet?.destroy) return sheet._apGestureSheet;

  let handle = sheet.querySelector('.ap-modal-handle');

  if (!handle) {
    handle = document.createElement('div');
    handle.className = 'ap-modal-handle';
    handle.setAttribute('aria-hidden', 'true');
    const content = sheet.querySelector('.modal-content, .ap-modal-content');
    sheet.insertBefore(handle, content || sheet.firstChild);
  }

  sheet.setAttribute(ENHANCED_ATTR, '1');
  sheet.style.setProperty('--ap-modal-y', '0px');
  handle.style.touchAction = 'none';
  sheet.style.touchAction = 'none';
  getContent();

  const state = {
    active: false,
    pending: false,
    pointerId: null,
    source: null,
    mode: 'touch',
    startY: 0,
    currentY: 0,
    startSheetY: 0,
    lastY: 0,
    lastTs: 0,
    velocityY: 0,
    raf: 0,
    boundMove: null,
    boundEnd: null,
  };

  function getContent() {
    const content = sheet.querySelector('.modal-content, .ap-modal-content') || sheet;
    content.style.overscrollBehavior = 'contain';
    content.style.webkitOverflowScrolling = 'touch';
    return content;
  }

  function getClientY(event) {
    const touch = event.touches?.[0] || event.changedTouches?.[0];
    return touch ? touch.clientY : 0;
  }

  function scheduleY(y) {
    const nextY = Math.max(0, y);
    if (state.raf) cancelAnimationFrame(state.raf);
    state.raf = requestAnimationFrame(() => {
      state.raf = 0;
      setSheetY(sheet, nextY);
      setBackdropProgress(nextY);
    });
  }

  function resetState() {
    if (state.raf) cancelAnimationFrame(state.raf);
    state.raf = 0;
    state.active = false;
    state.pending = false;
    state.pointerId = null;
    state.source = null;
    state.velocityY = 0;
    sheet.classList.remove('ap-modal-dragging');
  }

  function bindDocumentMove() {
    if (state.boundMove) return;
    state.boundMove = move;
    state.boundEnd = end;
    document.addEventListener('touchmove', state.boundMove, { passive: false });
    document.addEventListener('touchend', state.boundEnd, { passive: false });
    document.addEventListener('touchcancel', state.boundEnd, { passive: false });
  }

  function bindMouseMove() {
    if (state.boundMove) return;
    state.boundMove = moveMouse;
    state.boundEnd = endMouse;
    document.addEventListener('mousemove', state.boundMove, { passive: false });
    document.addEventListener('mouseup', state.boundEnd, { passive: false });
  }

  function unbindDocumentMove() {
    if (!state.boundMove) return;
    document.removeEventListener('touchmove', state.boundMove);
    document.removeEventListener('touchend', state.boundEnd);
    document.removeEventListener('touchcancel', state.boundEnd);
    document.removeEventListener('mousemove', state.boundMove);
    document.removeEventListener('mouseup', state.boundEnd);
    state.boundMove = null;
    state.boundEnd = null;
  }

  function beginGesture(y, mode) {
    state.pending = true;
    state.active = true;
    state.source = 'handle';
    state.mode = mode;
    state.startY = y;
    state.currentY = y;
    state.startSheetY = getSheetY(sheet);
    state.lastY = y;
    state.lastTs = performance.now();
    state.velocityY = 0;
    sheet.classList.add('ap-modal-dragging');
    sheet.classList.remove('ap-modal-settling', 'ap-modal-closing');
    setUiBusy(true);
  }

  function start(event) {
    if (!sheet.classList.contains('open') && !sheet.classList.contains('is-open')) return;

    const isHandle = event.target === handle || handle.contains(event.target);

    if (!isHandle && isMtaChatScrollZone(event.target)) return;

    if (!isHandle && isInteractiveTarget(event.target) && !isDraggableInteractiveTarget(sheet, event.target)) return;

    state.source = isHandle ? 'handle' : 'content';
    state.mode = 'touch';
    state.pending = true;
    state.active = isHandle;
    state.pointerId = 'touch';
    state.startY = getClientY(event);
    state.currentY = state.startY;
    state.startSheetY = getSheetY(sheet);
    state.lastY = state.startY;
    state.lastTs = performance.now();
    state.velocityY = 0;

    if (state.active) {
      sheet.classList.add('ap-modal-dragging');
      sheet.classList.remove('ap-modal-settling', 'ap-modal-closing');
      setUiBusy(true);
      event.preventDefault?.();
    }

    bindDocumentMove();

  }

  function startMouse(event) {
    if (event.button !== 0) return;
    if (!sheet.classList.contains('open') && !sheet.classList.contains('is-open')) return;
    if (!(event.target === handle || handle.contains(event.target))) return;

    event.preventDefault();
    beginGesture(event.clientY, 'mouse');
    bindMouseMove();
  }

  function move(event) {
    if (!state.pending) return;

    const y = getClientY(event);
    const deltaY = y - state.startY;

    if (!state.active) {
      if (state.source === 'content') {
        // touchstart on #mta-chat-list never binds (start() returns early). Any "content" drag is from
        // chrome (title, divider, …); do not gate on #mta-chat-list scrollTop or dismiss fails when list is scrolled.
        if (deltaY > 6) {
          // Pull-down to dismiss must not run until scroll is at top; otherwise it steals "scroll back up".
          const scrollTop = getModalBodyScrollTop(sheet);
          if (scrollTop > 2) {
            return;
          }
          state.active = true;
          sheet.classList.add('ap-modal-dragging');
          sheet.classList.remove('ap-modal-settling', 'ap-modal-closing');
          setUiBusy(true);
        } else if (Math.abs(deltaY) > 8) {
          resetState();
          unbindDocumentMove();
          return;
        } else {
          return;
        }
      }
    }

    if (!state.active) return;

    event.preventDefault?.();

    const now = performance.now();
    const dt = Math.max(1, now - state.lastTs);
    state.velocityY = (y - state.lastY) / dt;
    state.currentY = y;
    state.lastY = y;
    state.lastTs = now;

    scheduleY(state.startSheetY + deltaY);
  }

  function moveMouse(event) {
    if (!state.pending || state.mode !== 'mouse') return;
    event.preventDefault();

    const y = event.clientY;
    const deltaY = y - state.startY;
    const now = performance.now();
    const dt = Math.max(1, now - state.lastTs);
    state.velocityY = (y - state.lastY) / dt;
    state.currentY = y;
    state.lastY = y;
    state.lastTs = now;

    scheduleY(state.startSheetY + deltaY);
  }

  function getSnapTarget(deltaY, velocityY) {
    if (!supportsCompactSnap(sheet)) {
      const sheetHeight = Math.max(1, sheet.getBoundingClientRect().height);
      const shouldClose = deltaY > Math.min(180, sheetHeight * 0.28)
        || velocityY > 0.48
        || (deltaY / sheetHeight) > 0.32;
      return shouldClose ? 'close' : 0;
    }

    const compactY = getCompactY(sheet);
    const startY = state.startSheetY;
    const currentY = Math.max(0, startY + deltaY);
    const fromCompact = startY >= compactY - 12;

    if (fromCompact) {
      if (deltaY < -42 || velocityY < -0.28 || currentY < compactY * 0.72) return 0;
      if (deltaY > 80 || velocityY > 0.55 || currentY > compactY + 90) return 'close';
      return compactY;
    }

    if (deltaY > Math.min(160, Math.max(80, compactY * 0.22)) || velocityY > 0.36) {
      if (currentY > compactY + 90 || velocityY > 0.82) return 'close';
      return compactY;
    }

    return 0;
  }

  function end(event) {
    if (!state.pending) return;

    const deltaY = state.currentY - state.startY;
    const snapTarget = getSnapTarget(deltaY, state.velocityY);

    unbindDocumentMove();

    if (state.active && snapTarget === 'close') {
      resetState();
      closeSheet(sheet);
      return;
    }

    resetState();
    settleSheet(sheet, typeof snapTarget === 'number' ? snapTarget : 0);
  }

  function endMouse(event) {
    if (!state.pending || state.mode !== 'mouse') return;
    event.preventDefault();

    const deltaY = state.currentY - state.startY;
    const snapTarget = getSnapTarget(deltaY, state.velocityY);

    unbindDocumentMove();

    if (state.active && snapTarget === 'close') {
      resetState();
      closeSheet(sheet);
      return;
    }

    resetState();
    settleSheet(sheet, typeof snapTarget === 'number' ? snapTarget : 0);
  }

  sheet.addEventListener('touchstart', start, { passive: false });
  handle.addEventListener('mousedown', startMouse);

  const gesture = {
    destroy() {
      sheet.removeEventListener('touchstart', start);
      handle.removeEventListener('mousedown', startMouse);
      unbindDocumentMove();
      sheet._apGestureSheet = null;
      sheet.removeAttribute(ENHANCED_ATTR);
    },
  };

  sheet._apGestureSheet = gesture;

  return gesture;
}

export function enhanceLegacyModalGestures(root = document) {
  const gestures = [];

  root.querySelectorAll('.modal.bottom-sheet, .ap-modal.bottom-sheet').forEach((sheet) => {
    const gesture = enhanceBottomSheet(sheet);
    if (gesture) gestures.push(gesture);
  });

  return {
    destroy() {
      gestures.forEach((gesture) => gesture.destroy());
    },
  };
}
